import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import TouchAppRoundedIcon from "@mui/icons-material/TouchAppRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { UploadPlayground } from "./demo/UploadPlayground";
import { DemoUploadCard } from "./demo/DemoUploadCard";
import { isTourActive, stopActiveTour } from "./tourEngine";
import { useRunTour } from "./useStartTutorial";
import { useTutorialsActions } from "@/store";
import { TUTORIAL_STATUS } from "./decideTutorialPrompt";
import { trackTutorialEvent } from "./tutorialAnalytics";
import { PROGRESS_CHIP } from "./TutorialCard";

/**
 * Tutorial details popup — one vertical flow, top to bottom:
 *
 *   1. header (title, summary) — pinned while you scroll
 *   2. Start the tour + "How the tour goes": the numbered script, with the
 *      steps where the user has to click something marked — read it first,
 *      or just press Start
 *   3. "Try it": the playground (playground tutorials) / a preview (app tours)
 *   4. the article, for the details (collapsible)
 *
 * The tour itself runs inside this dialog for playground tutorials (driver.js
 * overlay sits above MUI's modal; the highlighted control stays clickable).
 * App-mode tutorials close the dialog first and run over the real screens.
 */

const stripTags = (html) => String(html || "").replace(/<[^>]+>/g, "");

const SectionTitle = ({ children }) => (
  <Typography
    component="h3"
    sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#F5C754", mb: 1.5 }}
  >
    {children}
  </Typography>
);

const Section = ({ children, sx, ...rest }) => (
  <Box sx={{ px: { xs: 2, sm: 3 }, py: 2.5, borderBottom: "1px solid rgba(255,255,255,0.08)", ...sx }} {...rest}>
    {children}
  </Box>
);

export const TutorialDialog = ({ open, tutorial, progress, autoStartTour = false, onClose, onAutoStartConsumed }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const runTour = useRunTour();
  const playgroundRef = useRef(null);
  const [tourRunning, setTourRunning] = useState(false);
  const [articleOpen, setArticleOpen] = useState(true);
  const [scriptOpen, setScriptOpen] = useState(false);
  const autoStartedRef = useRef(false);
  // True while a playground tour started by THIS dialog is running. App tours
  // outlive the dialog (it closes, the route changes), so we must not stop them.
  const ownsTourRef = useRef(false);

  const isPlayground = tutorial?.mode === "playground";
  const isArticle = tutorial?.mode === "article";
  const chip = PROGRESS_CHIP[progress] || PROGRESS_CHIP.NEW;
  const Article = tutorial?.Article;
  const { markTutorial } = useTutorialsActions();
  // Article tutorials: "Mark as read" = COMPLETED; closing without it = SKIPPED
  // (mirrors leaving a tour with Esc/✕). Ref, not state — handleClose must see
  // the click even when both fire in the same tick.
  const markedReadRef = useRef(false);

  const handleMarkRead = useCallback(() => {
    if (!tutorial) return;
    markedReadRef.current = true;
    markTutorial({ tutorialId: tutorial.id, status: TUTORIAL_STATUS.COMPLETED, version: tutorial.version });
    trackTutorialEvent("tutorial_completed", { tutorialId: tutorial.id, version: tutorial.version, source: "article" });
    onClose?.();
  }, [tutorial, markTutorial, onClose]);

  const handleClose = useCallback(() => {
    if (isTourActive()) return; // Esc / backdrop while a tour runs belongs to the tour
    // Closing an article unread records NOTHING — it stays "New" and the
    // post-login prompt keeps offering it. Only "Mark as read" completes it.
    if (isArticle && tutorial && !markedReadRef.current && progress !== "COMPLETED") {
      trackTutorialEvent("tutorial_article_closed_unread", { tutorialId: tutorial.id, version: tutorial.version });
    }
    onClose?.();
  }, [onClose, isArticle, tutorial, progress]);

  const startPlaygroundTour = useCallback(async () => {
    if (!tutorial || tourRunning) return;
    playgroundRef.current?.dispatch({ type: "reset" });
    // Bring the sandbox to the top of the popup so the whole card + controls are
    // in view before the first popover appears.
    const sandbox = document.querySelector('[data-tour="playground"]');
    if (sandbox && typeof sandbox.scrollIntoView === "function") {
      sandbox.scrollIntoView({ block: "start", behavior: "smooth" });
    }
    setTourRunning(true);
    ownsTourRef.current = true;
    try {
      const outcome = await runTour(tutorial, {
        source: autoStartTour ? "prompt" : "dialog",
        onStep: (step) => {
          if (!step.playground || !playgroundRef.current) return;
          if (step.playground.reset) playgroundRef.current.dispatch({ type: "reset" });
          const { reset, ...patch } = step.playground;
          if (Object.keys(patch).length) playgroundRef.current.dispatch({ type: "patch", patch });
        },
        // "Do it for me": replay the step's playground action(s) exactly as the
        // user's click would, so advanceWhen fires the same way.
        onAutoAct: (step) => {
          if (!step.action || !playgroundRef.current) return false;
          const actions = Array.isArray(step.action) ? step.action : [step.action];
          actions.forEach((action) => playgroundRef.current.dispatch(action));
          return true;
        },
      });
      // Done → back to the Tutorials overview. ✕ / Esc keep the popup open.
      if (outcome === "completed") onClose?.();
    } finally {
      ownsTourRef.current = false;
      setTourRunning(false);
    }
  }, [tutorial, tourRunning, runTour, autoStartTour, onClose]);

  const startAppTour = useCallback(() => {
    if (!tutorial) return;
    onClose?.();
    // Let the dialog unmount before the tour highlights the real screen.
    window.setTimeout(() => runTour(tutorial, { source: "dialog" }), 250);
  }, [tutorial, onClose, runTour]);

  // ?tour=1 → start once the playground has mounted. Latest callbacks live in
  // a ref so re-renders between scheduling and firing don't cancel the timer.
  const autoStartFnsRef = useRef({});
  autoStartFnsRef.current = { onAutoStartConsumed, startPlaygroundTour, startAppTour, isPlayground };
  useEffect(() => {
    if (!open || !autoStartTour || autoStartedRef.current || !tutorial) return undefined;
    if (tutorial.mode === "article") {
      // No tour to start — just clear ?tour=1 from the URL.
      onAutoStartConsumed?.();
      return undefined;
    }
    autoStartedRef.current = true;
    let fired = false;
    const handle = window.setTimeout(() => {
      fired = true;
      const fns = autoStartFnsRef.current;
      fns.onAutoStartConsumed?.();
      if (fns.isPlayground) fns.startPlaygroundTour();
      else fns.startAppTour();
    }, 400);
    // Closed / unmounted before it fired (Esc within 400 ms) → never start a
    // tour for a dialog that no longer exists. If the timer was cancelled
    // before firing (incl. StrictMode's dev double-mount), allow a re-arm.
    return () => {
      window.clearTimeout(handle);
      if (!fired) autoStartedRef.current = false;
    };
  }, [open, autoStartTour, tutorial]);

  useEffect(() => {
    if (!open) autoStartedRef.current = false;
    if (open) markedReadRef.current = false;
  }, [open]);

  // Closing / switching the popup ends the playground tour running inside it.
  useEffect(() => {
    if (!open && ownsTourRef.current) stopActiveTour();
    return () => {
      if (ownsTourRef.current) stopActiveTour();
    };
  }, [open]);

  if (!tutorial) return null;

  const startLabel = tourRunning ? "Tour running…" : progress === "COMPLETED" ? "Replay the tour" : "Start the tour";
  const yourTurnCount = tutorial.steps.filter((s) => s.advanceWhen).length;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      fullScreen={fullScreen}
      disableEnforceFocus
      disableRestoreFocus
      data-testid="tutorial-dialog"
      PaperProps={{
        sx: {
          background: "#1B2635",
          color: "#F7FAFA",
          borderRadius: fullScreen ? 0 : "14px",
          border: "1px solid rgba(255,255,255,0.1)",
          height: fullScreen ? "100%" : "min(90vh, 960px)",
        },
      }}
    >
      {/* Pinned header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 2,
          px: { xs: 2, sm: 3 },
          pt: 2.5,
          pb: 2,
          borderBottom: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography sx={{ fontSize: 20, fontWeight: 700 }}>{tutorial.title}</Typography>
            <Chip
              size="small"
              label={chip.label}
              sx={{ height: 22, fontSize: 11, fontWeight: 700, letterSpacing: "0.03em", textTransform: "uppercase", backgroundColor: chip.bg, color: chip.fg, borderRadius: "6px" }}
            />
          </Box>
          <Typography sx={{ fontSize: 14, color: "rgba(247,250,250,0.72)", mt: 0.5 }}>{tutorial.summary}</Typography>
        </Box>
        <IconButton onClick={handleClose} aria-label="Close" data-testid="tutorial-dialog-close" sx={{ color: "rgba(247,250,250,0.7)", mt: -0.5, mr: -1 }}>
          <CloseRoundedIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        {/* 1. Start + script (tours only — an article has neither) */}
        {!isArticle && (
        <Section data-testid="tutorial-dialog-script">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
              p: 2,
              mb: 2.5,
              borderRadius: 2,
              background: "rgba(245,199,84,0.08)",
              border: "1px solid rgba(245,199,84,0.35)",
            }}
          >
            <Button
              variant="contained"
              size="large"
              startIcon={<PlayArrowRoundedIcon />}
              onClick={isPlayground ? startPlaygroundTour : startAppTour}
              disabled={tourRunning}
              data-testid="tutorial-dialog-start-tour"
              sx={{ borderRadius: "10px", fontWeight: 800, textTransform: "none", whiteSpace: "nowrap", px: 2.5, background: "#F5C754", color: "#1B2432", "&:hover": { background: "#FFD873" } }}
            >
              {startLabel}
            </Button>
            <Box sx={{ minWidth: 200, flex: 1 }}>
              <Typography sx={{ fontSize: 14, color: "#F7FAFA", fontWeight: 600 }}>
                About {tutorial.estimatedMinutes} min · {tutorial.steps.length} steps
                {yourTurnCount > 0 ? ` · ${yourTurnCount} where you click something` : ""}
              </Typography>
              <Typography sx={{ fontSize: 13, color: "rgba(247,250,250,0.7)" }}>
                {isPlayground
                  ? "Runs right here, in the sandbox below — nothing touches a real upload. Each step tells you what to do, and a “Do it for me” button is always there."
                  : "Runs on the real upload screen: this window closes and the tour points at the actual buttons."}
              </Typography>
            </Box>
          </Box>

          <Box
            role="button"
            tabIndex={0}
            onClick={() => setScriptOpen((v) => !v)}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setScriptOpen((v) => !v)}
            aria-expanded={scriptOpen}
            data-testid="tutorial-dialog-script-toggle"
            sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer", userSelect: "none" }}
          >
            <SectionTitle>How the tour goes</SectionTitle>
            <Typography sx={{ fontSize: 12, color: "rgba(247,250,250,0.55)", mb: 1.5 }}>
              {scriptOpen ? "hide the steps" : `show the ${tutorial.steps.length} steps`}
            </Typography>
            <ExpandMoreRoundedIcon
              sx={{ mb: 1.5, color: "rgba(247,250,250,0.6)", transform: scriptOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}
            />
          </Box>
          <Collapse in={scriptOpen} timeout={200}>
          <Box component="ol" sx={{ m: 0, p: 0, listStyle: "none", display: "grid", gap: 1 }}>
            {tutorial.steps.map((step, i) => (
              <Box
                component="li"
                key={`${tutorial.id}-${i}`}
                data-testid={`tutorial-script-step-${i}`}
                sx={{ display: "grid", gridTemplateColumns: "28px 1fr", gap: 1.25, alignItems: "start" }}
              >
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    fontSize: 12,
                    fontWeight: 800,
                    background: step.advanceWhen ? "rgba(245,199,84,0.2)" : "rgba(255,255,255,0.08)",
                    color: step.advanceWhen ? "#F5C754" : "rgba(247,250,250,0.8)",
                    mt: "1px",
                  }}
                >
                  {i + 1}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#F7FAFA" }}>{step.title}</Typography>
                    {step.advanceWhen && (
                      <Chip
                        size="small"
                        icon={<TouchAppRoundedIcon sx={{ fontSize: 14, color: "#F5C754 !important" }} />}
                        label="you click"
                        sx={{ height: 20, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.03em", textTransform: "uppercase", backgroundColor: "rgba(245,199,84,0.14)", color: "#F5C754", borderRadius: "6px" }}
                      />
                    )}
                  </Box>
                  <Typography sx={{ fontSize: 13, color: "rgba(247,250,250,0.7)", lineHeight: 1.45 }}>
                    {stripTags(step.description)}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
          </Collapse>
        </Section>
        )}

        {/* 2. Playground / preview (tours only) */}
        {!isArticle && (
        <Section data-testid="tutorial-dialog-side">
          <SectionTitle>{isPlayground ? "Try it" : "Preview"}</SectionTitle>
          {isPlayground ? (
            <>
              <Typography sx={{ fontSize: 13.5, color: "rgba(247,250,250,0.75)", mb: 1.5 }}>
                A sandbox copy of the upload card. Change the connection, fire events, press the card&apos;s own
                buttons — the tour plays here too.
              </Typography>
              {/* While a tour runs, the popover is the only explanation: the
                  sandbox shows just what the real app would show. */}
              <UploadPlayground ref={playgroundRef} showExplain={!tourRunning} />
            </>
          ) : (
            <Box sx={{ p: 2, borderRadius: 2, backgroundColor: "rgba(41,56,77,0.7)", border: "1px solid rgba(255,255,255,0.08)", maxWidth: 560 }}>
              <DemoUploadCard state={tutorial.previewState || "uploading"} expandFiles={false} />
            </Box>
          )}
        </Section>
        )}

        {/* 3. Article — the whole tutorial when mode is "article" (always open,
            with Mark as read); a collapsible appendix after a tour otherwise */}
        <Section data-testid="tutorial-dialog-article" sx={{ borderBottom: "none" }}>
          {isArticle ? (
            <>
              {Article ? <Article /> : null}
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mt: 2.5 }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<CheckCircleRoundedIcon />}
                  onClick={handleMarkRead}
                  data-testid="tutorial-dialog-mark-read"
                  sx={{ borderRadius: "10px", fontWeight: 800, textTransform: "none", px: 2.5, background: "#F5C754", color: "#1B2432", "&:hover": { background: "#FFD873" } }}
                >
                  {progress === "COMPLETED" ? "Done" : "Got it — mark as read"}
                </Button>
                {progress !== "COMPLETED" && (
                  <Typography sx={{ fontSize: 12.5, color: "rgba(247,250,250,0.6)" }}>
                    Marks this tutorial as completed so it stops being offered after sign-in.
                  </Typography>
                )}
              </Box>
            </>
          ) : (
            <>
              <Box
                role="button"
                tabIndex={0}
                onClick={() => setArticleOpen((v) => !v)}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setArticleOpen((v) => !v)}
                aria-expanded={articleOpen}
                data-testid="tutorial-dialog-article-toggle"
                sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer", userSelect: "none" }}
              >
                <SectionTitle>Read the details</SectionTitle>
                <ExpandMoreRoundedIcon
                  sx={{ mb: 1.5, color: "rgba(247,250,250,0.6)", transform: articleOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}
                />
              </Box>
              <Collapse in={articleOpen} timeout={200}>
                {Article ? <Article /> : null}
              </Collapse>
            </>
          )}
        </Section>
      </DialogContent>
    </Dialog>
  );
};
